import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import Layout from "../components/Layout";
import apiFetch from "../utils/apiFetch";

const isMeaningful = (v) =>
  v !== null && v !== undefined && String(v).trim() !== "";

const AccidentAddInfo = () => {
  const { type, id } = useParams(); // type: "substation" | "network"
  const navigate = useNavigate();
  const location = useLocation();

  const [accident, setAccident] = useState(location.state?.accident || null);
  const [loading, setLoading] = useState(!location.state?.accident);

  const [formValues, setFormValues] = useState({
    event_date: "",
    repair_date: "",
    topic: "",
    protection_type: "",
    recorder_reading: "",
    cause: "",
    measures: "",
    result: "",
    employee_details: "",
  });

  const [files, setFiles] = useState([]);
  const [employeeSearch, setEmployeeSearch] = useState("");
  const [employeeOptions, setEmployeeOptions] = useState([]);
  const [selectedEmployees, setSelectedEmployees] = useState([]);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [apiError, setApiError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  const token = useMemo(() => localStorage.getItem("token"), []);
  const authHeader = useMemo(
    () => (token ? { Authorization: `Bearer ${token}` } : {}),
    [token]
  );

  // Fallback: если страницу открыли напрямую без state — подгружаем список и находим запись.
  useEffect(() => {
    const load = async () => {
      if (accident || !type || !id) return;

      setLoading(true);
      try {
        const listUrl =
          type === "substation" ? "/api/substation-accidents" : "/api/network-accidents";

        const res = await apiFetch(listUrl, { cache: "no-store" });
        const data = await res.json();
        const found = Array.isArray(data)
          ? data.find((x) => String(x.id) === String(id))
          : null;

        if (!found) {
          setApiError("Авария топилмади.");
        } else {
          setAccident(found);
        }
      } catch (e) {
        setApiError("Сервер билан боғланишда хатолик.");
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [accident, type, id, authHeader]);

  // employee autocomplete
  useEffect(() => {
    let aborted = false;

    const run = async () => {
      const q = employeeSearch.trim();
      if (!q) {
        setEmployeeOptions([]);
        return;
      }

      try {
        const res = await apiFetch(`/api/employees?search=${encodeURIComponent(q)}`);
        const data = await res.json();
        setEmployeeOptions(Array.isArray(data) ? data : []);
      } catch {
        if (!aborted) setEmployeeOptions([]);
      }
    };

    const t = setTimeout(run, 250);
    return () => {
      aborted = true;
      clearTimeout(t);
    };
  }, [employeeSearch, authHeader]);

  const isNetwork = type === "network";

  const displayObjectName = useMemo(() => {
    if (!accident) return "—";
    if (type === "substation") return accident?.sub_accident?.name || accident?.substation?.name || accident?.sub_accident_id || "—";
    return accident?.network_id_accident_network?.name || accident?.network?.name || accident?.network_id_accident || "—";
  }, [accident, type]);

  const displayFilial = useMemo(() => {
    const obj =
      type === "substation"
        ? accident?.sub_accident || accident?.substation
        : accident?.network_id_accident_network || accident?.network;

    return obj?.city?.name || "";
  }, [accident, type]);

  const handleSelectEmployee = (emp) => {
    if (!emp?.id) return;
    setSelectedEmployees((prev) => {
      if (prev.some((x) => x.id === emp.id)) return prev;
      return [...prev, emp];
    });
    setEmployeeSearch("");
    setEmployeeOptions([]);
  };

  const removeEmployee = (empId) => {
    setSelectedEmployees((prev) => prev.filter((x) => x.id !== empId));
  };

  const clearEmployees = () => {
    setSelectedEmployees([]);
    setEmployeeSearch("");
    setEmployeeOptions([]);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormValues((p) => ({ ...p, [name]: value }));
  };

  const handleFilesChange = (e) => {
    setFiles(Array.from(e.target.files || []));
  };

  const hasAnythingToSave = useMemo(() => {
    const anyField = Object.values(formValues).some((v) => isMeaningful(v));
    return anyField || files.length > 0 || selectedEmployees.length > 0;
  }, [formValues, files, selectedEmployees]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setApiError("");
    setSuccessMessage("");

    if (!hasAnythingToSave) {
      setApiError("Камида битта майдон/фото/ходим киритинг.");
      return;
    }

    setIsSubmitting(true);
    try {
      const fd = new FormData();

      // даты
      if (isMeaningful(formValues.event_date)) {
        fd.append(isNetwork ? "date" : "event_date", formValues.event_date);
      }
      if (isMeaningful(formValues.repair_date)) {
        fd.append("repair_date", formValues.repair_date);
      }

      // поля
      const map = {
        topic: "topic",
        protection_type: "protection_type",
        recorder_reading: "recorder_reading",
        cause: "cause",
        measures: "measures",
        result: "result",
        employee_details: "employee_details",
      };

      Object.entries(map).forEach(([k]) => {
        if (isMeaningful(formValues[k])) fd.append(k, String(formValues[k]).trim());
      });

      // сотрудники
      if (selectedEmployees.length > 0) {
        const ids = selectedEmployees.map((x) => x.id).filter(Boolean);
        fd.append("employee_ids", JSON.stringify(ids));
      }

      // файлы
      files.forEach((f) => fd.append("files", f));

      const url =
        type === "substation"
          ? `/api/substation-accidents/${id}/updates`
          : `/api/network-accidents/${id}/updates`;

      const res = await apiFetch(url, {
        method: "POST",
        body: fd, // FormData
      });

      if (!res.ok) {
        const text = await res.text();
        console.error("Update accident error:", text);
        setApiError("Хатолик юз берди. Кейинроқ уриниб кўринг.");
        return;
      }

      setSuccessMessage("Малумот сақланди ✅");
      setFormValues({
        event_date: "",
        repair_date: "",
        topic: "",
        protection_type: "",
        recorder_reading: "",
        cause: "",
        measures: "",
        result: "",
        employee_details: "",
      });
      setFiles([]);
      clearEmployees();

      // удобно: возвращаем назад к списку
      setTimeout(() => 
        navigate("/accidents", {
            replace: true,
            state: { refreshToken: Date.now() },
        }), 
        500);
    } catch (err) {
      console.error(err);
      setApiError("Сервер билан боғланишда хатолик.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const filledFieldsCount = Object.values(formValues).filter((v) => isMeaningful(v)).length;
  const eventDate =
    accident?.event_date ||
    accident?.date ||
    accident?.event_datetime ||
    accident?.date_created ||
    "";
  const currentTopic = accident?.topic || accident?.protection_type || "";

  return (
    <Layout>
      <div className="container">
        <main className="accident-add accident-add--info">
          <section className="accident-add__hero">
            <div>
              <span className="accident-add__kicker">Қўшимча маълумот</span>
              <h1 className="accident-add__title">Малумот қўшиш</h1>
              <p>
                Мавжуд ўчиш ёзувига янги сана, сабаб, чоралар ёки фото
                материалларни қўшиш.
              </p>
            </div>

            <div className="accident-add__status-grid">
              <div className="accident-add__status-card">
                <span>Тури</span>
                <strong>{type === "substation" ? "Подстанция" : "Линия"}</strong>
              </div>
              <div className="accident-add__status-card">
                <span>ID</span>
                <strong>#{id}</strong>
              </div>
              <div className="accident-add__status-card">
                <span>Тўлдирилган</span>
                <strong>{filledFieldsCount}</strong>
              </div>
              <div className="accident-add__status-card">
                <span>Медиа</span>
                <strong>{files.length}</strong>
              </div>
            </div>
          </section>

          {apiError && (
            <div className="accident-add__alert accident-add__alert--error">
              {apiError}
            </div>
          )}

          {loading && <div className="accident-add__alert">Юкланмоқда...</div>}

          <form className="accident-add__form" onSubmit={handleSubmit}>
            <section className="accident-add__section">
              <div className="accident-add__section-head">
                <div>
                  <span className="accident-add__section-number">01</span>
                  <h2>Контекст</h2>
                </div>
                <p>Қайси объект ва ёзувга маълумот қўшилаётгани.</p>
              </div>

              <div className="accident-add__object-info">
                <div className="accident-add__object-title">
                  {type === "substation" ? "Подстанция" : "Линия"}:
                  <span>{displayObjectName}</span>
                </div>

                {displayFilial ? (
                  <div className="accident-add__object-meta">
                    Филиал: <span>{displayFilial}</span>
                  </div>
                ) : null}

                {(eventDate || currentTopic) && (
                  <div className="accident-add__object-meta">
                    {eventDate ? <span>Сана: {eventDate}</span> : null}
                    {eventDate && currentTopic ? <span> · </span> : null}
                    {currentTopic ? <span>{currentTopic}</span> : null}
                  </div>
                )}
              </div>
            </section>

            <section className="accident-add__section">
              <div className="accident-add__section-head">
                <div>
                  <span className="accident-add__section-number">02</span>
                  <h2>Янги маълумот</h2>
                </div>
                <p>Фақат киритилган майдонлар update сифатида сақланади.</p>
              </div>

              <div className="accident-add__form-grid">
                <div className="accident-add__field">
                  <label>{type === "substation" ? "Авария вақти" : "Авария вақти"}</label>
                  <input
                    type="datetime-local"
                    name="event_date"
                    value={formValues.event_date}
                    onChange={handleChange}
                    className="accident-add__input"
                  />
                </div>

                <div className="accident-add__field">
                  <label>Ремонт вақти</label>
                  <input
                    type="datetime-local"
                    name="repair_date"
                    value={formValues.repair_date}
                    onChange={handleChange}
                    className="accident-add__input"
                  />
                </div>

                <div className="accident-add__field accident-add__field--full">
                  <label>Авария тури</label>
                  <textarea
                    name="topic"
                    value={formValues.topic}
                    onChange={handleChange}
                    className="accident-add__textarea"
                    rows={3}
                  />
                </div>

                <div className="accident-add__field accident-add__field--full">
                  <label>Ишлаган ҳимоя тури</label>
                  <input
                    type="text"
                    name="protection_type"
                    value={formValues.protection_type}
                    onChange={handleChange}
                    className="accident-add__input"
                  />
                </div>

                {isNetwork && (
                  <div className="accident-add__field accident-add__field--full">
                    <label>Мослама кўрсаткичи</label>
                    <input
                      type="text"
                      name="recorder_reading"
                      value={formValues.recorder_reading}
                      onChange={handleChange}
                      className="accident-add__input"
                    />
                  </div>
                )}

                <div className="accident-add__field accident-add__field--full">
                  <label>Аниқланган сабаби</label>
                  <textarea
                    name="cause"
                    value={formValues.cause}
                    onChange={handleChange}
                    className="accident-add__textarea"
                    rows={3}
                  />
                </div>

                <div className="accident-add__field accident-add__field--full">
                  <label>Кўрилган чоралар</label>
                  <textarea
                    name="measures"
                    value={formValues.measures}
                    onChange={handleChange}
                    className="accident-add__textarea"
                    rows={3}
                  />
                </div>

                {isNetwork && (
                  <div className="accident-add__field accident-add__field--full">
                    <label>АҚУ натижаси</label>
                    <textarea
                      name="result"
                      value={formValues.result}
                      onChange={handleChange}
                      className="accident-add__textarea"
                      rows={2}
                    />
                  </div>
                )}

              </div>
            </section>

            <section className="accident-add__section">
              <div className="accident-add__section-head">
                <div>
                  <span className="accident-add__section-number">03</span>
                  <h2>Фото</h2>
                </div>
                <p>Қўшимча файллар update ёзувига бириктирилади.</p>
              </div>

              <div className="accident-add__files">
                <div className="accident-add__files-label">Фото (ихтиёрий)</div>
                <input
                  type="file"
                  multiple
                  onChange={handleFilesChange}
                  className="accident-add__files-input"
                />
                {files.length > 0 && (
                  <ul className="accident-add__files-list">
                    {files.map((f) => (
                      <li key={f.name} className="accident-add__files-item">
                        {f.name}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </section>

            {successMessage && (
              <div className="accident-add__alert accident-add__alert--success">
                {successMessage}
              </div>
            )}

            <div className="accident-add__actions">
              <button
                type="submit"
                className="accident-add__submit"
                disabled={!hasAnythingToSave || isSubmitting}
              >
                {isSubmitting ? "Сақланмоқда..." : "Малумот сақлаш"}
              </button>
            </div>
          </form>
        </main>
      </div>
    </Layout>
  );
};

export default AccidentAddInfo;
